import { useRef, useCallback, useState, useEffect } from 'react'
import { Mail, MessageCircle, Pencil } from 'lucide-react'
import { TIER_VOLUMES } from '../lib/pricing-tiers'

interface PricingSliderProps {
  label: string
  servicio: 'correo' | 'whatsapp' | 'correo_whatsapp'
  valor: number
  onChange: (v: number) => void
}

const UNIT_LABELS: Record<string, string> = {
  correo: 'emails/mes',
  whatsapp: 'mensajes/mes',
  correo_whatsapp: 'emails + mensajes/mes',
}

// Calcular porcentaje basado en el volumen (interpolando entre tiers)
function volumenAPorcentaje(vol: number): number {
  if (vol <= 0) return 0
  if (vol >= TIER_VOLUMES[TIER_VOLUMES.length - 1]) return 100

  for (let i = 0; i < TIER_VOLUMES.length; i++) {
    if (vol === TIER_VOLUMES[i]) {
      return (i / (TIER_VOLUMES.length - 1)) * 100
    }
  }

  // Interpolar entre tiers
  for (let i = 0; i < TIER_VOLUMES.length - 1; i++) {
    if (vol > TIER_VOLUMES[i] && vol < TIER_VOLUMES[i + 1]) {
      const ratio = (vol - TIER_VOLUMES[i]) / (TIER_VOLUMES[i + 1] - TIER_VOLUMES[i])
      const lower = (i / (TIER_VOLUMES.length - 1)) * 100
      const upper = ((i + 1) / (TIER_VOLUMES.length - 1)) * 100
      return lower + ratio * (upper - lower)
    }
  }

  return 0
}

// Calcular tier index más cercano desde porcentaje
function porcentajeATierIndex(pct: number): number {
  const idx = Math.round((pct / 100) * (TIER_VOLUMES.length - 1))
  return Math.max(0, Math.min(TIER_VOLUMES.length - 1, idx))
}

export function PricingSlider({ label, servicio, valor, onChange }: PricingSliderProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [inputValue, setInputValue] = useState(String(valor))
  const isDragging = useRef(false)

  useEffect(() => {
    if (!isEditing) {
      setInputValue(String(valor))
    }
  }, [valor, isEditing])

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!containerRef.current) return
      isDragging.current = true
      const rect = containerRef.current.getBoundingClientRect()
      let pct = ((e.clientX - rect.left) / rect.width) * 100
      pct = Math.max(0, Math.min(100, pct))
      const tierIdx = porcentajeATierIndex(pct)
      onChange(TIER_VOLUMES[tierIdx])
    },
    [onChange]
  )

  const handlePointerUp = useCallback(() => {
    document.removeEventListener('pointermove', handlePointerMove)
    document.removeEventListener('pointerup', handlePointerUp)
    setTimeout(() => {
      isDragging.current = false
    }, 100)
  }, [handlePointerMove])

  const handleThumbDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault()
      e.stopPropagation()
      isDragging.current = false
      document.addEventListener('pointermove', handlePointerMove)
      document.addEventListener('pointerup', handlePointerUp)
    },
    [handlePointerMove, handlePointerUp]
  )

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging.current) return
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      let pct = ((e.clientX - rect.left) / rect.width) * 100
      pct = Math.max(0, Math.min(100, pct))
      const tierIdx = porcentajeATierIndex(pct)
      onChange(TIER_VOLUMES[tierIdx])
    },
    [onChange]
  )

  const handleEditClick = () => {
    setIsEditing(true)
    setInputValue(String(valor))
    setTimeout(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    }, 0)
  }

  const handleInputBlur = () => {
    setIsEditing(false)
    const num = parseInt(inputValue) || 0
    // Aceptar cualquier valor entre 0 y el máximo tier
    const max = TIER_VOLUMES[TIER_VOLUMES.length - 1]
    onChange(Math.max(0, Math.min(num, max)))
  }

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      ;(e.target as HTMLInputElement).blur()
    }
  }

  const percentage = volumenAPorcentaje(valor)

  return (
    <div
      className="p-5 rounded-xl border border-gray-300 dark:border-gray-700 transition-all duration-300 hover:shadow-md hover:border-teal-500 group"
      style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {servicio === 'correo' && <Mail className="h-5 w-5 text-teal-700" />}
          {servicio === 'whatsapp' && <MessageCircle className="h-5 w-5 text-teal-500" />}
          {servicio === 'correo_whatsapp' && (
            <>
              <Mail className="h-[18px] w-[18px] text-teal-700" />
              <MessageCircle className="h-[18px] w-[18px] text-teal-500" />
            </>
          )}
          <h3 className="text-base font-semibold text-gray-900">{label}</h3>
        </div>
        <div className="text-right">
          <div className="relative inline-block">
            <Pencil
              className="absolute -left-7 top-1/2 -translate-y-1/2 h-4 w-4 text-teal-500 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              onClick={handleEditClick}
            />
            <input
              ref={inputRef}
              type="number"
              className={`text-2xl font-bold text-gray-900 rounded px-2 py-1 text-right bg-transparent border-none outline-none w-[110px] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield] ${isEditing ? 'ring-1 ring-teal-300 bg-teal-50/50' : 'cursor-pointer'}`}
              value={isEditing ? inputValue : valor}
              readOnly={!isEditing}
              onChange={(e) => {
                const val = e.target.value
                setInputValue(val)
                const num = parseInt(val) || 0
                const max = TIER_VOLUMES[TIER_VOLUMES.length - 1]
                onChange(Math.max(0, Math.min(num, max)))
              }}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              onClick={() => {
                if (!isEditing) handleEditClick()
              }}
              onFocus={(e) => {
                if (!isEditing) handleEditClick()
                setTimeout(() => {
                  e.target.select()
                }, 0)
              }}
              min={0}
              step={1}
            />
          </div>
          <div className="text-xs text-gray-500 mt-1">{UNIT_LABELS[servicio]}</div>
        </div>
      </div>

      {/* Custom Slider */}
      <div
        ref={containerRef}
        className="relative w-full h-2 bg-gray-200 rounded cursor-pointer mb-5 select-none"
        onClick={handleTrackClick}
      >
        {/* Fill */}
        <div
          className="absolute h-full rounded transition-[width] duration-300"
          style={{
            width: `${percentage}%`,
            background: 'linear-gradient(90deg, #0d9488 0%, #2dd4bf 100%)',
          }}
        />

        {/* Thumb */}
        <div
          className="absolute w-5 h-5 bg-white rounded-full top-1/2 -translate-y-1/2 cursor-pointer z-[2] hover:scale-[1.2] transition-transform"
          style={{
            left: `${percentage}%`,
            transform: `translate(-50%, -50%)`,
            border: '3px solid #0d9488',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
          onPointerDown={handleThumbDown}
        />

        {/* Markers */}
        <div className="absolute top-full left-0 right-0 h-3 pointer-events-none">
          {TIER_VOLUMES.map((_, i) => (
            <div
              key={i}
              className="absolute w-px h-3 bg-gray-400 top-0"
              style={{
                left: `${(i / (TIER_VOLUMES.length - 1)) * 100}%`,
                transform: 'translateX(-50%)',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
